import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { Typography } from "@foodpilot/foods";
import type { Permission } from "src/api/client-api/permissions/useGetAllPermissions";
import { groupPermissionsByObject, matchesSearch, resolveCoreColumns } from "../permissionGrouping";
import { PermissionResourceRow } from "./PermissionResourceRow";

const RESOURCE_COLUMN_MIN_PX = 200;
const ACTION_COLUMN_MIN_PX = 96;
const SPECIFIC_COLUMN_MIN_PX = 280;
const COLUMN_GAP_PX = 12;
const GRID_OUTER_PADDING_PX = 32;

const computeGridMinWidth = (coreColumnCount: number): number => {
    const columnWidths = RESOURCE_COLUMN_MIN_PX + coreColumnCount * ACTION_COLUMN_MIN_PX + SPECIFIC_COLUMN_MIN_PX;
    const totalColumnCount = coreColumnCount + 2;
    const gaps = (totalColumnCount - 1) * COLUMN_GAP_PX;

    return columnWidths + gaps + GRID_OUTER_PADDING_PX;
};

export type PermissionGridProps = {
    scopedPermissions: Permission[];
    selectedIds: Set<string>;
    search: string;
    activeOnly: boolean;
    onToggle: (permissionId: string) => void;
    onSelectAll: (object: string) => void;
    onSelectNone: (object: string) => void;
};

export const PermissionGrid = (props: PermissionGridProps) => {
    const { scopedPermissions, selectedIds, search, activeOnly, onToggle, onSelectAll, onSelectNone } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const isWide = useMediaQuery(theme.breakpoints.up("lg"));
    const stacked = !isWide;

    const allGroups = useMemo(() => groupPermissionsByObject(scopedPermissions), [scopedPermissions]);
    const coreColumns = useMemo(() => resolveCoreColumns(allGroups), [allGroups]);

    const visibleGroups = useMemo(() => {
        return allGroups.filter((group) => {
            const matchesQuery = group.permissions.some((permission) => matchesSearch(permission, search));

            if (!matchesQuery) {
                return false;
            }

            if (activeOnly) {
                return group.permissions.some((permission) => selectedIds.has(permission["@id"]));
            }

            return true;
        });
    }, [allGroups, search, activeOnly, selectedIds]);

    const columnTemplate = useMemo(() => {
        const resourceCell = `minmax(${RESOURCE_COLUMN_MIN_PX}px, 1.4fr)`;
        const coreCells = coreColumns.map(() => `minmax(${ACTION_COLUMN_MIN_PX}px, 1fr)`).join(" ");
        const specificCell = `minmax(${SPECIFIC_COLUMN_MIN_PX}px, 2fr)`;
        return `${resourceCell} ${coreCells} ${specificCell}`;
    }, [coreColumns]);

    const gridMinWidth = useMemo(() => computeGridMinWidth(coreColumns.length), [coreColumns]);

    const headerCellSx = (muiTheme: typeof theme) => ({
        textTransform: "uppercase" as const,
        letterSpacing: "0.04em",
        color: muiTheme.custom.grey[2500],
    });

    return (
        <Box
            role="table"
            aria-label={t("settings.roles.column.resource", "Resource")}
            sx={(theme) => ({
                border: `1px solid ${theme.custom.grey[600]}`,
                borderRadius: theme.spacing(1.5),
                overflow: "hidden",
                maxWidth: "100%",
                backgroundColor: theme.custom.grey[100],
            })}
        >
            <Box
                sx={(theme) => ({
                    overflowX: "auto",
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": {
                        height: theme.spacing(0.75),
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: theme.custom.grey[600],
                        borderRadius: theme.spacing(0.5),
                    },
                })}
            >
                <Box sx={{ minWidth: stacked ? "auto" : gridMinWidth }}>
                    {stacked ? null : (
                        <Box
                            role="row"
                            sx={(theme) => ({
                                display: "grid",
                                gridTemplateColumns: columnTemplate,
                                alignItems: "center",
                                columnGap: theme.spacing(1.5),
                                padding: theme.spacing(1.5, 2),
                                backgroundColor: theme.custom.grey[300],
                            })}
                        >
                            <Typography role="columnheader" variant="small-bold" sx={headerCellSx}>
                                {t("settings.roles.column.resource", "Resource")}
                            </Typography>
                            {coreColumns.map((slug) => (
                                <Typography
                                    role="columnheader"
                                    key={slug}
                                    variant="small-bold"
                                    align="center"
                                    sx={(theme) => ({
                                        fontFamily: "var(--font-mono, monospace)",
                                        color: theme.custom.grey[2500],
                                    })}
                                >
                                    {slug}
                                </Typography>
                            ))}
                            <Typography role="columnheader" variant="small-bold" sx={headerCellSx}>
                                {t("settings.roles.column.specific", "Specific")}
                            </Typography>
                        </Box>
                    )}

                    {visibleGroups.length === 0 ?
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            sx={(theme) => ({ padding: theme.spacing(5, 2) })}
                        >
                            <Typography variant="body-medium" sx={(theme) => ({ color: theme.custom.grey[2500] })}>
                                {t("settings.roles.empty_match", "No resource matches.")}
                            </Typography>
                        </Stack>
                    :   visibleGroups.map((group) => (
                            <PermissionResourceRow
                                key={group.object}
                                object={group.object}
                                coreColumns={coreColumns}
                                corePermissions={group.core}
                                specificPermissions={group.specific}
                                selectedIds={selectedIds}
                                onToggle={onToggle}
                                onSelectAll={onSelectAll}
                                onSelectNone={onSelectNone}
                                columnTemplate={columnTemplate}
                                stacked={stacked}
                            />
                        ))
                    }
                </Box>
            </Box>
        </Box>
    );
};
